import React, { useState } from 'react';
import { Brain, Lock, User, LogIn, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { login } from './authService';
import { useDesignSystem, cn } from './designSystem';

const Login = ({ onLoginSuccess, onSwitchToRegistration }) => {
  const { typography, componentStyles, effects } = useDesignSystem();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Get user data from login
      const userData = await login(username, password);
      
      // Just call the callback with the user data
      // Let App.jsx handle environment updates
      onLoginSuccess(userData);
    } catch (err) {
      setError(err.message || 'Kunne ikke logge inn. Vennligst prøv igjen senere.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 p-4">
      <Card className={cn(
        componentStyles.card.glass,
        "w-full max-w-md border-none shadow-lg"
      )}>
        <CardHeader className="pb-4 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-4 rounded-xl">
              <Brain className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle className={typography.gradient + " text-2xl font-bold"}>
            Skribo
          </CardTitle>
          <p className="text-sm text-blue-600/80 dark:text-blue-400/60 italic">
            Dere sier, Boien skriver
          </p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className={componentStyles.alert.error + " flex items-center space-x-2 text-sm"}>
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="username" className={typography.label}>
                Brukernavn
              </Label>
              <div className="relative">
                <div className={componentStyles.input.icon.wrapper}>
                  <User className={componentStyles.input.icon.base} />
                </div>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={cn(
                    componentStyles.input.base,
                    componentStyles.input.withIcon
                  )}
                  placeholder="Skriv inn brukernavn"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className={typography.label}>
                Passord
              </Label>
              <div className="relative">
                <div className={componentStyles.input.icon.wrapper}>
                  <Lock className={componentStyles.input.icon.base} />
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={cn(
                    componentStyles.input.base,
                    componentStyles.input.withIcon
                  )}
                  placeholder="Skriv inn passord"
                  required
                />
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={isLoading}
              className={cn(
                componentStyles.button.base,
                componentStyles.button.primary,
                componentStyles.button.sizes.md,
                "w-full",
                isLoading && componentStyles.button.states.loading
              )}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className={cn(
                    "mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full",
                    effects.spin
                  )}></div>
                  Logger inn...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <LogIn className="mr-2 h-4 w-4" />
                  Logg inn
                </div>
              )}
            </Button>
            
            {/* Registration link - Added inside the form */}
            <div className="text-center mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Har du ikke en konto ennå?{' '}
                <button 
                  type="button"
                  onClick={onSwitchToRegistration}
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  Registrer deg
                </button>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;